import { render } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import './styles/tokens.css';
import './styles/app.css';
import { SearchPalette } from './components/SearchPalette';
import { Toasts } from './components/ui';
import { api, ApiError, BASE, setCsrf, type Me } from './lib/api';
import { Link, match, Redirect, useLocation } from './lib/router';
import { prefs, session } from './lib/store';
import { Browse } from './pages/Browse';
import { Capture } from './pages/Capture';
import { CardView } from './pages/CardView';
import { Collection, Collections } from './pages/Collections';
import { CookMode } from './pages/CookMode';
import { Editor } from './pages/Editor';
import { History } from './pages/History';
import { Home } from './pages/Home';
import { Settings } from './pages/Settings';
import { Trash } from './pages/Trash';

const AUTH_ERRORS: Record<string, string> = {
  no_access: 'Your Identity DHM account does not have access to Bakery. Ask an administrator to grant you the owner role on Bakery.',
  expired: 'That sign-in took too long. Please try again.',
  failed: 'Sign-in failed. Please try again.',
  unavailable: 'Identity DHM could not be reached. Please try again in a moment.',
  not_configured: 'Sign-in is not configured on this server yet (see docs/deploy.md).',
};

function signInHref(me: Me, path: string) {
  return `${me.login_url}?returnTo=${encodeURIComponent(`${BASE}${path === '/' ? '/' : path}`)}`;
}

/** Shown in place of an admin-only page for visitors who are not signed in. */
function SignInGate({ me, path }: { me: Me; path: string }) {
  return (
    <section class="gate" aria-labelledby="gate-h">
      <p class="dr-label">Admin</p>
      <h1 id="gate-h" class="dr-title">
        Sign in to make changes<span class="dr-stop">.</span>
      </h1>
      <p class="muted">Anyone can browse and bake from Bakery. Adding, editing and settings are for the owner.</p>
      {me.login_available ? (
        <a class="dr-btn dr-btn--primary" href={signInHref(me, path)}>
          Sign in with Identity DHM
        </a>
      ) : (
        <p class="muted">{AUTH_ERRORS.not_configured}</p>
      )}
    </section>
  );
}

const PUBLIC_NAV: [string, string, string][] = [
  ['/', 'Home', '⌂'],
  ['/browse', 'Browse', '▦'],
  ['/collections', 'Collections', '❏'],
];
const ADMIN_NAV: [string, string, string][] = [
  ['/capture', 'Capture', '+'],
  ['/settings', 'Settings', '⚙'],
];

function isActive(href: string, path: string) {
  return href === '/' ? path === '/' : path.startsWith(href);
}

function Shell({ me }: { me: Me }) {
  const { path, query } = useLocation();
  const [palette, setPalette] = useState(false);
  const admin = me.authenticated;
  const authError = query.get('auth_error');
  const nav = admin ? [...PUBLIC_NAV, ...ADMIN_NAV] : PUBLIC_NAV;

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) {
        e.preventDefault();
        setPalette(true);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const gate = <SignInGate me={me} path={path} />;
  let page;
  let m: Record<string, string> | null;
  if (path === '/') page = <Home />;
  else if (path === '/browse') page = <Browse query={query} />;
  else if (path === '/search') page = <Browse query={query} />;
  else if ((m = match('/c/:id/bake', path))) return <CookMode id={m.id} />;
  else if ((m = match('/c/:id/cook', path))) return <Redirect to={`/c/${m.id}/bake`} />; // old links and bookmarks
  else if ((m = match('/c/:id/edit', path))) page = admin ? <Editor id={m.id} /> : gate;
  else if ((m = match('/c/:id/history', path))) page = admin ? <History id={m.id} /> : gate;
  else if ((m = match('/c/:id/:slug?', path))) page = <CardView id={m.id} />;
  else if ((m = match('/new/:type', path))) page = admin ? <Editor type={m.type} /> : gate;
  else if (path === '/collections') page = <Collections />;
  else if ((m = match('/collections/:id', path))) page = <Collection id={m.id} />;
  else if (path === '/capture') page = admin ? <Capture /> : gate;
  else if (path === '/trash') page = admin ? <Trash /> : gate;
  else if (path === '/settings') page = admin ? <Settings me={me} /> : gate;
  else
    page = (
      <div class="empty">
        <h1 class="dr-title">Not found</h1>
        <Link href="/" class="dr-link">
          Go home
        </Link>
      </div>
    );

  return (
    <>
      <a href="#main" class="skip-link">
        Skip to content
      </a>
      <header class="topbar">
        <Link href="/" class="dr-wordmark" aria-label="Bakery home">
          DHM <i class="dr-wordmark__tone">Bakery</i>
          <span class="dr-stop">.</span>
        </Link>
        <nav class="dr-nav topnav" aria-label="Main">
          {nav.map(([href, label]) => (
            <Link href={href} aria-current={isActive(href, path) ? 'page' : undefined}>
              {label}
            </Link>
          ))}
        </nav>
        <div class="topbar-end">
          <button type="button" class="dr-btn bk-ghost bk-sm search-trigger" onClick={() => setPalette(true)} aria-label="Search (⌘K or /)">
            Search <span class="kbd">⌘K</span>
          </button>
          {!admin && me.login_available && (
            <a class="dr-btn bk-ghost bk-sm" href={signInHref(me, path)}>
              Sign in
            </a>
          )}
        </div>
      </header>
      <main id="main">
        {authError && (
          <p class="error" role="alert">
            {AUTH_ERRORS[authError] ?? AUTH_ERRORS.failed}
          </p>
        )}
        {page}
      </main>
      <footer class="site-footer">
        Bakery v{__APP_VERSION__} · Updated{' '}
        <time dateTime={__APP_UPDATED__}>{new Date(__APP_UPDATED__).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</time>
      </footer>
      <nav class="bottomnav" aria-label="Main">
        {nav.map(([href, label, icon]) => (
          <Link href={href} aria-current={isActive(href, path) ? 'page' : undefined}>
            <div aria-hidden="true">{icon}</div>
            {label}
          </Link>
        ))}
      </nav>
      {palette && <SearchPalette onClose={() => setPalette(false)} />}
    </>
  );
}

function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const p = prefs.use();

  useEffect(() => {
    document.documentElement.dataset.theme = p.theme;
  }, [p.theme]);

  const load = () =>
    api
      .get<Me>('/auth/me')
      .then((m) => {
        setCsrf(m.csrf_token);
        session.set(m);
        setMe(m);
      })
      .catch((e) => setError(String(e.message ?? e)));

  useEffect(() => {
    void load();
  }, []);

  if (error)
    return (
      <main id="main">
        <p class="error">Bakery is unreachable: {error}</p>
      </main>
    );
  if (!me) return null;
  return (
    <>
      <Shell me={me} />
      <Toasts />
    </>
  );
}

// A 401 means the admin session ended (signed out elsewhere, role removed): reload as a visitor.
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason instanceof ApiError && e.reason.status === 401) location.reload();
});

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.register(`${BASE}/sw.js`, { scope: `${BASE}/` }).catch(() => {});
}

render(<App />, document.getElementById('app')!);
